/**
 * Payment Transaction Management Service
 *
 * Handles payment lifecycle operations including transaction management,
 * reconciliation, and subscription control. This service acts as the
 * orchestration layer between Stripe payment processing and our business logic.
 *
 * Security Architecture:
 * - All payment operations require authenticated users (token validation)
 * - Payment IDs never exposed in URLs (prevents log disclosure)
 * - Backend validates ownership before any payment modifications
 * - Idempotent operations to prevent double processing
 *
 * Transaction Integrity:
 * - Two-phase confirmation pattern (create → confirm) prevents race conditions
 * - Reconciliation process ensures payment state consistency
 * - Webhook events provide authoritative payment status
 * - Audit trail maintained for all payment operations
 *
 * Business Logic Patterns:
 * - Payment lifecycle: Created → Confirmed → Captured/Cancelled
 * - Subscription lifecycle: Active → Cancelled → Expired
 * - Reconciliation: Sync local state with Stripe truth source
 *
 * Compliance Requirements:
 * - PCI DSS: No sensitive payment data stored locally
 * - GDPR: Payment history retention per data policy
 * - Financial regulations: Audit trail and reconciliation
 *
 * @module apiPayments
 * @see {@link .claude/security-rules.md} - Payment security standards
 * @see {@link docs/09_PAYMENT_SUBSCRIPTION_SYSTEM.md} - Payment architecture
 */

import { getData, postData } from './apiFetch';
import type {
  PaymentHistoryResponse,
  PaymentCancellationResponse,
  PaymentIntentResponse,
  ReconciliationResponse,
} from '../types/api';

/**
 * Retrieves payment history for authenticated user.
 *
 * WHY authentication required:
 * - Payment history is sensitive financial data
 * - Prevents enumeration of other users' transactions
 * - Required for PCI compliance and privacy regulations
 * - Enables user-specific filtering and pagination
 *
 * Security considerations:
 * - Returns empty array for unauthenticated users (no error)
 *   WHY: Prevents information disclosure about authentication state
 * - Backend filters to only user's payments (prevents data leakage)
 * - Sensitive fields (full card number) never included
 * - Payment IDs obfuscated in response
 *
 * @returns {Promise<PaymentHistoryResponse>} User's payment history or empty array
 * @security Only returns payments belonging to authenticated user
 */
export async function getPayments(): Promise<PaymentHistoryResponse> {
  const token = localStorage.getItem('accessToken');

  // WHY return empty array instead of error:
  // Prevents information disclosure about authentication requirements.
  // UI can handle empty state gracefully without error boundaries.
  if (!token) return [];
  const path = `payments`;
  const payments = await getData<PaymentHistoryResponse>({ path });

  return payments;
}

/**
 * Cancels a pending payment before capture.
 *
 * WHY cancellation vs refund:
 * - Cancellation prevents charge (no fees incurred)
 * - Refund reverses completed charge (fees not returned)
 * - Immediate fund release vs 5-10 day refund process
 * - Better user experience for abandoned carts
 *
 * Security flow:
 * 1. Backend validates user owns the payment
 * 2. Checks payment status (must be pending/authorized)
 * 3. Cancels with Stripe and updates local state
 * 4. Triggers notification to user
 *
 * Idempotency:
 * - Multiple calls with same paymentId are safe
 * - Already cancelled payments return success (idempotent)
 * - Prevents double-cancellation race conditions
 *
 * @param {string} paymentId - Internal payment ID (not Stripe ID)
 * @returns {Promise<PaymentCancellationResponse>} Cancellation confirmation
 * @security Backend validates ownership before cancellation
 */
export async function cancelPayment(paymentId: string): Promise<PaymentCancellationResponse> {
  // WHY paymentId in URL path: RESTful convention for resource operations,
  // but still validates ownership server-side
  const path = `payments/${paymentId}/cancel`;
  // WHY empty object in data: POST requires body, might extend with reason later
  const result = await postData<PaymentCancellationResponse>({ path, data: {} });
  return result;
}

/**
 * Confirms a payment after user authorization (3D Secure, etc).
 *
 * WHY two-phase commit pattern:
 * - Create phase: Initialize payment with amount and details
 * - Confirm phase: Execute after user authentication/authorization
 * - Prevents charge without explicit user action
 * - Handles asynchronous authentication flows (3D Secure)
 *
 * Security critical:
 * - Confirmation requires matching session and payment owner
 * - Amount cannot be modified during confirmation
 * - Idempotency key prevents double charges
 * - Backend validates against original payment intent
 *
 * Flow:
 * 1. Payment created with amount and items
 * 2. User authenticates (3D Secure if required)
 * 3. Confirm payment (this function)
 * 4. Webhook confirms final status
 *
 * @param {object} data - Confirmation data including payment intent ID
 * @returns {Promise<PaymentIntentResponse>} Payment confirmation with status
 * @security CRITICAL: Backend must validate amount hasn't changed
 */
export async function confirmPayment(data: {
  paymentIntentID: string;
  [key: string]: unknown;
}): Promise<PaymentIntentResponse> {
  const path = `payments/confirm`;
  const result = await postData<PaymentIntentResponse>({ path, data });
  return result;
}

/**
 * Reconciles payment status with Stripe (source of truth).
 *
 * WHY reconciliation needed:
 * - Network failures can cause state inconsistency
 * - Webhooks might fail or be delayed
 * - User might complete payment in another tab/device
 * - Provides manual sync option for support cases
 *
 * Reconciliation process:
 * 1. Fetch current status from Stripe
 * 2. Compare with local database state
 * 3. Update local state to match Stripe
 * 4. Trigger appropriate business logic (fulfillment, notifications)
 *
 * Use cases:
 * - Payment shown as pending but actually completed
 * - Webhook delivery failure recovery
 * - Manual intervention by support team
 * - Periodic consistency checks
 *
 * @param {object} data - Payment identification for reconciliation
 * @returns {Promise<ReconciliationResponse>} Updated payment status
 * @security Only reconciles payments owned by authenticated user
 */
export async function reconPayment(data: {
  paymentId: string;
  [key: string]: unknown;
}): Promise<ReconciliationResponse> {
  // WHY "reconcile" not "sync": Financial industry terminology,
  // implies authoritative source (Stripe) and local adjustment
  const path = `payments/reconcile`;
  const result = await postData<ReconciliationResponse>({ path, data });
  return result;
}

/**
 * Cancels an active subscription.
 *
 * WHY cancellation complexity:
 * - Immediate vs end-of-period cancellation options
 * - Prorated refunds for annual plans
 * - Retention offers before cancellation
 * - Regulatory requirements (easy cancellation)
 *
 * Cancellation flow:
 * 1. Validate subscription ownership
 * 2. Determine cancellation type (immediate/period-end)
 * 3. Cancel with Stripe
 * 4. Update local subscription state
 * 5. Trigger retention/win-back workflows
 *
 * Business logic:
 * - Access continues until period end (unless immediate)
 * - Prorated refunds calculated server-side
 * - Reactivation possible before period end
 * - Dunning process stopped immediately
 *
 * @param {object} data - Subscription cancellation details
 * @returns {Promise<unknown>} Cancellation confirmation with end date
 * @security Requires recent authentication for high-value subscriptions
 */
export async function cancelSubscription(data: {
  subscriptionId: string;
  cancelImmediately?: boolean;
  cancellationReason?: string;
  [key: string]: unknown;
}): Promise<unknown> {
  const path = `subscriptions/cancel`;
  const result = await postData({ path, data });
  return result;
}
